import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Components
import DeleteButton from "../../../../shared/components/DeleteButton";
import PaginationNumeric from "../../../../shared/components/PaginationNumeric";
import AuthenticatedLayout from "../../../../shared/layouts/AuthenticatedLayout";
import theme from "../../../../shared/utils/themeColors";
import DifficultyBadge from "../../../question-bank/components/DifficultyBadge";

// Hooks
import { useTutorialBank } from "../../hooks/useTutorialBank";

// Types
import {
  getTestStatusBadgeClass,
  getTestStatusText,
} from "@shared/utils/test.utils";

function TutorialBankList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  // Memoize the parameters for useTutorialBank to prevent unnecessary re-renders
  const tutorialBankParams = useMemo(() => {
    return {
      page: currentPage,
      limit: itemsPerPage,
    };
  }, [currentPage, itemsPerPage]);

  // Use our custom hook for tutorial bank items with pagination
  const {
    items,
    totalItems,
    loading,
    error,
    refresh,
    deleteTutorial,
    getCategoryNameById,
  } = useTutorialBank(tutorialBankParams.page, tutorialBankParams.limit);

  // Handle search
  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  // Handle delete
  const handleDelete = useCallback(
    async (id: string) => {
      deleteTutorial(id);
    },
    [deleteTutorial]
  );

  // Handle edit
  const handleEdit = useCallback(
    (id: string) => {
      navigate(`/tutorial-bank/admin/edit/${id}`);
    },
    [navigate]
  );

  // Handle add new
  const handleAddNew = useCallback(() => {
    navigate("/tutorial-bank/admin/create");
  }, [navigate]);

  // Calculate total pages for pagination
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      // Prevent redundant page changes
      if (page === currentPage) return;
      setCurrentPage(page);
    },
    [currentPage]
  );

  // Truncate text to specified length with ellipsis
  const truncateText = useCallback((text: string, maxLength: number) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  }, []);

  return (
    <AuthenticatedLayout
      isLoading={loading}
      error={error}
      isEmpty={(items || []).length === 0}
      emptyStateProps={{
        title: "No tutorials found",
        description: searchTerm
          ? "No tutorials match your search criteria"
          : "Start by adding your first tutorial",
        icon: (
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-gray-200 to-gray-100 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className={`w-8 h-8 ${theme.text.muted}`}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        ),
      }}
      pageHeader={{
        title: "Tutorial Bank",
        /* search: {
          show: true,
          placeholder: "Search tutorials...",
          onChange: handleSearch,
        }, */
        refresh: {
          show: true,
          onRefresh: refresh,
        },
        actions: [
          {
            label: "Add Tutorial",
            onClick: handleAddNew,
          },
        ],
      }}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Tutorials table */}
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-sm border border-gray-200 dark:border-gray-700 relative">
          <header className="px-5 py-4">
            <h2 className="font-semibold text-gray-800 dark:text-gray-100">
              All Tutorials{" "}
              <span className="text-gray-400 dark:text-gray-500 font-medium">
                {totalItems}
              </span>
            </h2>
          </header>
          <div>
            {items.length > 0 ? (
              <table className="table-auto w-full">
                <thead className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/20 border-t border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Tutorial</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Category</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Difficulty</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Video</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Status</div>
                    </th>
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-center">Actions</div>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-gray-200 dark:divide-gray-700">
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-2 first:pl-5 last:pr-5 py-3">
                        <div className="text-left">
                          <div className="font-medium text-gray-800 dark:text-gray-100">
                            {truncateText(item.header?.title || "", 100)}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                            {truncateText(item.question?.text || "", 150)}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-left">
                          {getCategoryNameById(item.categoryId)}
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-left">
                          <DifficultyBadge difficulty={item.difficulty} />
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-left">
                          {item.video ? (
                            <span className="text-green-500 dark:text-green-400">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 inline-block mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              Available
                            </span>
                          ) : (
                            <span className="text-red-500 dark:text-red-400">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 inline-block mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                              Missing
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-left">
                          <div
                            className={`text-xs inline-flex font-medium rounded-full text-center px-2.5 py-1 ${getTestStatusBadgeClass(
                              item.status
                            )}`}
                          >
                            {getTestStatusText(item.status)}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-center flex justify-center space-x-2">
                          <button
                            className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400"
                            onClick={() => handleEdit(item.id!)}
                          >
                            <span className="sr-only">Edit</span>
                            <svg
                              className="w-8 h-8 fill-current"
                              viewBox="0 0 32 32"
                            >
                              <path d="M19.7 8.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM12.6 22H10v-2.6l6-6 2.6 2.6-6 6zm7.4-7.4L17.4 12l1.6-1.6 2.6 2.6-1.6 1.6z" />
                            </svg>
                          </button>
                          <DeleteButton
                            onDelete={() => handleDelete(item.id!)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8">
            <PaginationNumeric
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
}

export default TutorialBankList;
